import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Building2, CheckIcon, FileText, Search, Users } from "lucide-react";

import Login from "js/pages/Auth/Login";
import SignUp, { signUpSteps } from "js/pages/Auth/SignUp";
import ForgotPassword from "js/pages/Auth/ForgotPassword";
import LoginProtectedRoute from "js/components/Middleware/LoginProtectedRoute";
import LOGO_WHITE from "assets/logo-white.svg";

const features = [
  {
    icon: Building2,
    title: "Gestión de Propiedades",
    description: "Administra todas tus propiedades en un solo lugar",
  },
  // TODO: Habilitar cuando la funcionalidad esté disponible
  // {
  //   icon: FileText,
  //   title: "Contratos Digitales",
  //   description: "Crea y firma contratos de forma rápida y segura",
  // },
  {
    icon: Search,
    title: "Investigaciones",
    description: "Verifica a tus inquilinos con reportes completos",
  },
  {
    icon: Users,
    title: "Gestión de Inquilinos",
    description: "Mantén un registro organizado de todos tus inquilinos",
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const Authentication = () => {
  const { pathname } = useLocation();
  const isSignUp = pathname === "/signup";
  const isLogin = pathname === "/login" || pathname === "/";
  const isForgotPassword = pathname === "/forgot-password";
  const [currentStep, setCurrentStep] = useState(1);

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  return (
    <div className="flex min-h-screen">
      {/* Left Section - Branding or Step Indicator */}
      <motion.div
        className={`relative hidden overflow-hidden lg:flex`}
        initial={false}
        animate={{
          width: isSignUp ? "380px" : "50%",
        }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        style={{
          background:
            "linear-gradient(135deg, #0f0c4a 0%, #161467 50%, #1e1a7a 100%)",
        }}
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient mesh */}
          <div
            className="absolute -top-40 -left-40 h-[700px] w-[700px] rounded-full opacity-50 blur-[100px]"
            style={{
              background:
                "radial-gradient(circle, rgba(124,58,237,0.6) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute -bottom-40 -right-40 h-[600px] w-[600px] rounded-full opacity-40 blur-[100px]"
            style={{
              background:
                "radial-gradient(circle, rgba(79,70,229,0.6) 0%, transparent 70%)",
            }}
          />

          {/* Top right pattern - Isometric blueprint grid */}
          <svg
            className="absolute -top-6 -right-6 h-[320px] w-[320px] opacity-[0.08]"
            viewBox="0 0 320 320"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Isometric grid lines - going down-left */}
            <line x1="320" y1="0" x2="160" y2="92" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="40" x2="120" y2="155" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="80" x2="80" y2="218" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="120" x2="40" y2="282" stroke="white" strokeWidth="0.5" />
            <line x1="280" y1="0" x2="0" y2="161" stroke="white" strokeWidth="0.5" />
            <line x1="240" y1="0" x2="0" y2="138" stroke="white" strokeWidth="0.5" />
            <line x1="200" y1="0" x2="0" y2="115" stroke="white" strokeWidth="0.5" />

            {/* Isometric grid lines - going down-right */}
            <line x1="320" y1="0" x2="200" y2="69" stroke="white" strokeWidth="0.5" />
            <line x1="280" y1="0" x2="120" y2="92" stroke="white" strokeWidth="0.5" />
            <line x1="240" y1="0" x2="40" y2="115" stroke="white" strokeWidth="0.5" />
            <line x1="200" y1="0" x2="0" y2="115" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="60" x2="160" y2="152" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="120" x2="200" y2="189" stroke="white" strokeWidth="0.5" />

            {/* Isometric building/property shapes */}
            {/* Building 1 - top */}
            <path d="M260 40 L300 63 L300 103 L260 80 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <path d="M260 40 L220 63 L220 103 L260 80 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <path d="M220 63 L260 40 L300 63 L260 86 Z" stroke="white" strokeWidth="0.75" fill="none" />

            {/* Building 2 - smaller */}
            <path d="M180 90 L210 106 L210 136 L180 120 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M180 90 L150 106 L150 136 L180 120 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M150 106 L180 90 L210 106 L180 122 Z" stroke="white" strokeWidth="0.5" fill="none" />

            {/* Floor plan element */}
            <rect x="240" y="140" width="50" height="35" stroke="white" strokeWidth="0.5" fill="none" transform="skewY(-30) skewX(0)" />
            <line x1="265" y1="110" x2="265" y2="135" stroke="white" strokeWidth="0.5" strokeDasharray="2 2" />

            {/* Measurement marks */}
            <circle cx="300" cy="63" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="260" cy="40" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="220" cy="63" r="2" fill="white" fillOpacity="0.3" />
          </svg>

          {/* Bottom left pattern - Isometric blueprint grid */}
          <svg
            className="absolute -bottom-6 -left-6 h-[280px] w-[280px] opacity-[0.08]"
            viewBox="0 0 280 280"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Isometric grid lines */}
            <line x1="0" y1="280" x2="140" y2="199" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="240" x2="160" y2="148" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="200" x2="180" y2="96" stroke="white" strokeWidth="0.5" />
            <line x1="40" y1="280" x2="200" y2="188" stroke="white" strokeWidth="0.5" />
            <line x1="80" y1="280" x2="240" y2="188" stroke="white" strokeWidth="0.5" />

            {/* Cross lines */}
            <line x1="0" y1="280" x2="100" y2="222" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="240" x2="80" y2="194" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="200" x2="60" y2="165" stroke="white" strokeWidth="0.5" />

            {/* Property outline - floor plan style */}
            <path d="M40 240 L80 218 L120 240 L120 270 L80 292 L40 270 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <line x1="80" y1="218" x2="80" y2="248" stroke="white" strokeWidth="0.5" />

            {/* Room divisions */}
            <line x1="60" y1="229" x2="60" y2="255" stroke="white" strokeWidth="0.5" strokeDasharray="3 2" />
            <line x1="100" y1="229" x2="100" y2="255" stroke="white" strokeWidth="0.5" strokeDasharray="3 2" />

            {/* Small building element */}
            <path d="M150 220 L175 206 L175 236 L150 250 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M150 220 L125 206 L125 236 L150 250 Z" stroke="white" strokeWidth="0.5" fill="none" />

            {/* Grid intersection markers */}
            <circle cx="80" cy="218" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="40" cy="240" r="1.5" fill="white" fillOpacity="0.3" />
            <circle cx="120" cy="240" r="1.5" fill="white" fillOpacity="0.3" />
          </svg>

          {/* Subtle noise texture */}
          <div
            className="absolute inset-0 opacity-[0.25]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              mixBlendMode: "overlay",
            }}
          />
        </div>

        {/* Content */}
        <div
          className={`relative z-10 flex w-full flex-col justify-between p-8 text-white xl:p-12 ${
            isSignUp ? "items-start" : "items-center"
          }`}
        >
          {/* Logo */}
          <a href="https://multiburo.com.mx/">
            <img
              src={LOGO_WHITE}
              alt="Multiburo"
              className="absolute left-8 top-8 h-14 w-auto xl:left-12 xl:top-12 xl:h-16"
            />
          </a>

          {/* Main Content */}
          <AnimatePresence mode="wait">
            {isSignUp ? (
              // Step Indicator for SignUp
              <motion.div
                key="steps"
                className="flex flex-1 flex-col justify-center py-8"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="space-y-6">
                  {signUpSteps.map((step, index) => {
                    const isCompleted = currentStep > step.id;
                    const isCurrent = currentStep === step.id;

                    return (
                      <motion.div
                        key={step.id}
                        className="flex gap-4"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.3 }}
                      >
                        {/* Step indicator */}
                        <div className="flex flex-col items-center">
                          <motion.div
                            className={`flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors duration-300 ${
                              isCompleted
                                ? "border-white bg-white text-primary"
                                : isCurrent
                                  ? "border-white bg-white/20 text-white"
                                  : "border-white/30 bg-transparent text-white/50"
                            }`}
                            animate={{
                              scale: isCurrent ? 1.05 : 1,
                            }}
                            transition={{ duration: 0.2 }}
                          >
                            <AnimatePresence mode="wait">
                              {isCompleted ? (
                                <motion.div
                                  key="check"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  exit={{ scale: 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <CheckIcon className="h-4 w-4" />
                                </motion.div>
                              ) : (
                                <motion.span
                                  key="number"
                                  className="text-sm font-semibold"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  exit={{ scale: 0 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  {step.id}
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </motion.div>
                          {/* Connector line */}
                          {index < signUpSteps.length - 1 && (
                            <motion.div
                              className="mt-2 w-0.5 bg-white/30"
                              initial={{ height: 0 }}
                              animate={{ height: 48 }}
                              transition={{ delay: 0.2, duration: 0.3 }}
                              style={{
                                backgroundColor: isCompleted
                                  ? "white"
                                  : "rgba(255,255,255,0.3)",
                              }}
                            />
                          )}
                        </div>

                        {/* Step content */}
                        <div className="pt-1.5">
                          <h3
                            className={`font-semibold transition-colors duration-300 ${
                              isCurrent || isCompleted
                                ? "text-white"
                                : "text-white/50"
                            }`}
                          >
                            {step.title}
                          </h3>
                          <p className="mt-0.5 text-sm text-white/60">
                            {step.description}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ) : (
              // Branding for Login/ForgotPassword
              <motion.div
                key="branding"
                className="flex flex-1 flex-col items-center justify-center"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ exit: { duration: 0.1 } }}
              >
                {/* Header */}
                <motion.div
                  className="mb-10 max-w-lg text-center"
                  variants={fadeInUp}
                  transition={{ duration: 0.4 }}
                >
                  <h2 className="mb-4 text-3xl font-bold leading-tight tracking-tight xl:text-4xl">
                    La plataforma integral para la gestión inmobiliaria
                  </h2>
                  <p className="text-lg text-white/70">
                    Simplifica la administración de tus propiedades, contratos e
                    inquilinos en un solo lugar.
                  </p>
                </motion.div>

                {/* Feature Cards */}
                <motion.div
                  className="flex max-w-lg flex-col gap-3"
                  variants={staggerContainer}
                >
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <motion.div
                        key={index}
                        className="flex items-center gap-4 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]"
                        style={{
                          background: "rgba(255, 255, 255, 0.06)",
                          backdropFilter: "blur(12px)",
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                        }}
                        variants={fadeInUp}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                          style={{ background: "rgba(255, 255, 255, 0.1)" }}
                        >
                          <Icon className="h-5 w-5 text-white/80" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-white">
                            {feature.title}
                          </h3>
                          <p className="text-sm text-white/50">
                            {feature.description}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="self-center text-center text-sm text-white/40">
            {new Date().getFullYear()} Todos los derechos reservados
          </div>
        </div>
      </motion.div>

      {/* Right Section - Form */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        {/* Mobile Progress Bar - Fixed at very top */}
        {isSignUp && (
          <div className="absolute left-0 right-0 top-0 h-1 bg-[#161467]/20 lg:hidden">
            <div
              className="h-full transition-all duration-500 ease-out"
              style={{
                width: `${(currentStep / signUpSteps.length) * 100}%`,
                background: "linear-gradient(135deg, #0f0c4a 0%, #161467 50%, #1e1a7a 100%)"
              }}
            />
          </div>
        )}

        <div className={`mx-auto w-full ${isSignUp ? "max-w-lg" : "max-w-md"}`}>
          <AnimatePresence mode="wait">
            {isLogin && (
              <motion.div
                key="login"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <LoginProtectedRoute>
                  <Login />
                </LoginProtectedRoute>
              </motion.div>
            )}

            {isSignUp && (
              <motion.div
                key="signup"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <LoginProtectedRoute>
                  <SignUp
                    currentStep={currentStep}
                    onStepChange={handleStepChange}
                  />
                </LoginProtectedRoute>
              </motion.div>
            )}

            {isForgotPassword && (
              <motion.div
                key="forgot"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <LoginProtectedRoute>
                  <ForgotPassword />
                </LoginProtectedRoute>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Authentication;
